import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import css from './YourPicks.module.css';

// Pick-3 locked decisions: up to 3 pending picks, each with one 24-hour reply window.
const MAX_PICKS = 3;
const LEAD_WINDOW_MS = 24 * 3600 * 1000;

const replyDeadline = pick => {
  if (pick.replaceableAt) return pick.replaceableAt;
  const sent = Date.parse(pick.sentAt);
  return Number.isNaN(sent) ? null : new Date(sent + LEAD_WINDOW_MS).toISOString();
};

const STATE_CHIP = {
  waiting: { id: 'YourPicks.waitingChip', className: 'chipWaiting' },
  responded: { id: 'YourPicks.respondedChip', className: 'chipResponded' },
  noReply: { id: 'YourPicks.noReplyChip', className: 'chipNoReply' },
};

/**
 * The homeowner's picks for a request, as three slots: each pending pick with its
 * status (waiting for a reply, replied, or no reply after 24 hours), and open slots for
 * the rest. A pick with no reply after 24 hours can be released so the homeowner can
 * choose someone else; releasing can't be undone, so it asks first.
 *
 * @component
 * @param {Object} props
 * @param {Array<Object>} props.picks vendorSelections, ideally with state / canReplace
 *   from /api/request-selections (a pick made just now may not have them yet)
 * @param {Array<Object>} [props.vendors] contractors on the page, for names of new picks
 * @param {Object} props.releaseInProgress { [vendorId]: true } while releasing
 * @param {Object} [props.releaseError] { vendorId } of the last failed release
 * @param {Function} props.onReplace called with the vendorId to release
 * @returns {JSX.Element}
 */
const YourPicks = ({ picks, vendors = [], releaseInProgress, releaseError, onReplace }) => {
  const intl = useIntl();
  const [confirmingId, setConfirmingId] = useState(null);
  const pending = (picks || []).filter(p => p.status === 'pending').slice(0, MAX_PICKS);
  const openSlots = MAX_PICKS - pending.length;

  const nameFor = pick => {
    const vendor = vendors.find(v => v.id === pick.vendorId);
    return (
      pick.vendorName ||
      vendor?.businessName ||
      vendor?.displayName ||
      intl.formatMessage({ id: 'YourPicks.unknownName' })
    );
  };

  return (
    <section className={css.root} aria-labelledby="your-picks-title">
      <div className={css.header}>
        <h2 id="your-picks-title" className={css.title}>
          <FormattedMessage id="YourPicks.title" />
        </h2>
        <span className={css.count}>
          <FormattedMessage
            id="YourPicks.count"
            values={{ count: pending.length, max: MAX_PICKS }}
          />
        </span>
      </div>

      <ul className={css.slots}>
        {pending.map(pick => {
          const state = pick.state || 'waiting';
          const chip = STATE_CHIP[state] || STATE_CHIP.waiting;
          const name = nameFor(pick);
          const deadline = replyDeadline(pick);
          const isConfirming = confirmingId === pick.vendorId;
          const inProgress = !!releaseInProgress[pick.vendorId];

          return (
            <li key={pick.vendorId} className={classNames(css.slot, css[`slot_${state}`])}>
              <div className={css.slotTop}>
                <span className={css.avatar} aria-hidden="true">
                  {name.charAt(0).toUpperCase()}
                </span>
                <span className={css.name}>{name}</span>
              </div>
              <span className={classNames(css.chip, css[chip.className])}>
                <FormattedMessage id={chip.id} />
              </span>
              <p className={css.detail}>
                {state === 'waiting' && deadline ? (
                  <FormattedMessage
                    id="YourPicks.waiting"
                    values={{
                      time: intl.formatDate(deadline, {
                        weekday: 'short',
                        hour: 'numeric',
                        minute: '2-digit',
                      }),
                    }}
                  />
                ) : null}
                {state === 'responded' ? <FormattedMessage id="YourPicks.responded" /> : null}
                {state === 'noReply' ? <FormattedMessage id="YourPicks.noReply" /> : null}
              </p>

              {pick.canReplace && !isConfirming ? (
                <button
                  type="button"
                  className={css.replaceButton}
                  onClick={() => setConfirmingId(pick.vendorId)}
                  aria-label={intl.formatMessage({ id: 'YourPicks.replaceAria' }, { name })}
                  disabled={inProgress}
                >
                  <FormattedMessage id="YourPicks.replace" />
                </button>
              ) : null}

              {pick.canReplace && isConfirming ? (
                <div className={css.confirm} role="group" aria-label={name}>
                  <p className={css.confirmText}>
                    <FormattedMessage id="YourPicks.confirmPrompt" values={{ name }} />
                  </p>
                  <div className={css.confirmButtons}>
                    <button
                      type="button"
                      className={css.confirmButton}
                      disabled={inProgress}
                      onClick={() => {
                        setConfirmingId(null);
                        onReplace(pick.vendorId);
                      }}
                    >
                      <FormattedMessage id="YourPicks.confirm" />
                    </button>
                    <button
                      type="button"
                      className={css.cancelButton}
                      onClick={() => setConfirmingId(null)}
                    >
                      <FormattedMessage id="YourPicks.cancel" />
                    </button>
                  </div>
                </div>
              ) : null}

              {releaseError?.vendorId === pick.vendorId ? (
                <p className={css.error} role="alert">
                  <FormattedMessage id="YourPicks.releaseError" />
                </p>
              ) : null}
            </li>
          );
        })}
        {Array.from({ length: openSlots }, (_, i) => (
          <li key={`open-${i}`} className={classNames(css.slot, css.openSlot)}>
            <span className={css.openTitle}>
              <FormattedMessage id="YourPicks.openSlot" />
            </span>
            <span className={css.openHint}>
              <FormattedMessage id="YourPicks.openSlotHint" />
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default YourPicks;
